/**
 * Frequency coordination — search 3a, "which transmitters are in use near here".
 *
 * **In development, admin-only.** The gate that matters is `require_admin` on the API;
 * the nav flag and the `AdminRoute` wrapper are UX.
 *
 * The layout is built around one asymmetry: **missing a nearby transmitter is harmful,
 * showing too many is merely conservative.** Three consequences that should survive any
 * redesign of this page:
 *
 * 1. **The coverage block is always rendered and never collapsible.** It says what the
 *    search cannot see. A coordination answer that looks complete and is not is the
 *    worst failure this feature has.
 * 2. **Section counts are always on screen even when the section is collapsed.** The
 *    counts come from a COUNT(*) over the whole circle, so nothing can be silently
 *    missing — a reader can see there are 412 mobile records without expanding them.
 * 3. **Every row states its own basis as a chip.** A band authorisation is never
 *    rendered as a channel, and an area record never carries a pinpoint distance.
 */
import { useState } from 'react'
import { sitesNear } from '../services/coordination'
import { geocodeQuery } from '../services/geocode'
import { Pagination } from '../components/Pagination'
import PageHeading from '../components/PageHeading'
import { COORDINATION_HELP } from '../content/pageHelp'
import type { CoordinationBasis, SitesNearResponse, TransmitterRow } from '../types'

const DEFAULT_RADIUS = 25
const MAX_RADIUS = 70

const BASIS_LABEL: Record<CoordinationBasis, string> = {
  fixed_site: 'Fixed transmitter sites',
  mobile_area: 'Mobile / area-of-operation authorisations',
  band_grant: 'Band authorisations spanning this window',
}

const BASIS_CHIP: Record<CoordinationBasis, string> = {
  fixed_site: 'bg-green-100 text-green-800',
  mobile_area: 'bg-amber-100 text-amber-800',
  band_grant: 'bg-purple-100 text-purple-800',
}

const BASIS_CHIP_LABEL: Record<CoordinationBasis, string> = {
  fixed_site: 'Fixed site',
  mobile_area: 'Area',
  band_grant: 'Band grant',
}

const SECTION_ORDER: CoordinationBasis[] = ['fixed_site', 'mobile_area', 'band_grant']

/** Compass point for a bearing, because "NE" reads faster than "41°". */
function compass(deg: number | null): string {
  if (deg === null) return ''
  const points = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
                  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']
  return points[Math.round(deg / 22.5) % 16]
}

function BasisChip({ basis }: { basis: CoordinationBasis }) {
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${BASIS_CHIP[basis]}`}>
      {BASIS_CHIP_LABEL[basis]}
    </span>
  )
}

/**
 * The frequency cell. A band row shows its RANGE and is never shown as a channel — the
 * v0.39.1 lesson, carried over: "is on this channel" and "may transmit here" are two
 * different answers and must not share a column's meaning.
 */
function FrequencyCell({ row }: { row: TransmitterRow }) {
  if (row.basis === 'band_grant') {
    return (
      <span className="font-mono text-sm">
        {row.band_low} – {row.band_high} MHz
        <span className="block text-xs text-gray-500 font-sans">band authorisation</span>
      </span>
    )
  }
  return <span className="font-mono text-sm">{row.frequency}</span>
}

/**
 * The distance cell. An area record gets no pinpoint: its centre distance and its
 * operating radius are shown instead, which is what the record actually says.
 */
function DistanceCell({ row }: { row: TransmitterRow }) {
  if (row.distance_miles !== null) {
    return (
      <span className="text-sm whitespace-nowrap">
        {row.distance_miles.toFixed(1)} mi {compass(row.bearing_degrees)}
      </span>
    )
  }
  if (row.centre_miles !== null) {
    return (
      <span className="text-sm">
        centre {row.centre_miles.toFixed(1)} mi {compass(row.bearing_degrees)}
        {row.radius_of_operation_miles && (
          <span className="block text-xs text-gray-500">
            {row.radius_of_operation_miles} mi operating radius
          </span>
        )}
      </span>
    )
  }
  return <span className="text-gray-400">—</span>
}

function RowTable({ rows }: { rows: TransmitterRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">Call sign</th>
            <th className="px-3 py-2 text-left">Licensee</th>
            <th className="px-3 py-2 text-left">Frequency</th>
            <th className="px-3 py-2 text-left">Basis</th>
            <th className="px-3 py-2 text-left">Distance</th>
            <th className="px-3 py-2 text-left">Site</th>
            <th className="px-3 py-2 text-left">Class / T-R</th>
            <th className="px-3 py-2 text-left">Power (out / ERP)</th>
            <th className="px-3 py-2 text-left">Emissions</th>
            <th className="px-3 py-2 text-left">Antenna</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr
              key={`${row.unique_system_identifier}-${row.location_number}-${row.antenna_number}-${row.frequency ?? row.band_low}`}
              className="align-top"
            >
              <td className="px-3 py-2 font-mono whitespace-nowrap">{row.call_sign}</td>
              <td className="px-3 py-2">
                {row.licensee_name}
                <span className="block text-xs text-gray-500">
                  {row.radio_service_code} · {row.license_status}
                  {row.expired_date ? ` · exp ${row.expired_date}` : ''}
                </span>
              </td>
              <td className="px-3 py-2"><FrequencyCell row={row} /></td>
              <td className="px-3 py-2"><BasisChip basis={row.basis} /></td>
              <td className="px-3 py-2"><DistanceCell row={row} /></td>
              <td className="px-3 py-2 text-xs">
                {row.location_address}
                <span className="block text-gray-500">
                  {[row.location_city, row.location_county, row.location_state]
                    .filter(Boolean).join(', ')}
                </span>
                <span className="block text-gray-400">
                  {row.ground_elevation_m ? `${row.ground_elevation_m} m AMSL` : ''}
                  {row.height_of_support_structure_m ? ` · struct ${row.height_of_support_structure_m} m` : ''}
                  {row.tower_registration_number ? ` · ASR ${row.tower_registration_number}` : ''}
                </span>
              </td>
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                {row.class_station_code} / {row.transmit_receive_code}
                {row.units_of_work ? (
                  <span className="block text-gray-500">{row.units_of_work} units</span>
                ) : null}
              </td>
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                {row.power_output ?? '—'} / {row.power_erp ?? '—'} W
              </td>
              <td className="px-3 py-2 text-xs font-mono">
                {row.emission_designators.join(', ') || '—'}
              </td>
              <td className="px-3 py-2 text-xs whitespace-nowrap">
                {row.antenna_aat_m ? `AAT ${row.antenna_aat_m} m` : '—'}
                <span className="block text-gray-500">
                  {row.antenna_gain_dbd ? `${row.antenna_gain_dbd} dBd` : ''}
                  {row.antenna_azimuth ? ` · ${row.antenna_azimuth}°` : ''}
                  {row.antenna_polarization ? ` · ${row.antenna_polarization}` : ''}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * One basis section. The COUNT is always visible; only the rows collapse.
 *
 * This is the design choice that replaced "a toggle, off by default" for mobile records:
 * a toggle produces a screen that looks complete and is not, and a coordinator who never
 * finds it never learns what was hidden. A collapsed section with its count on screen
 * gets the same noise control without the omission risk.
 */
function BasisSection({
  basis, count, rows, open, onToggle, pageIncomplete,
}: {
  basis: CoordinationBasis
  count: number
  rows: TransmitterRow[]
  open: boolean
  onToggle: () => void
  pageIncomplete: boolean
}) {
  return (
    <section className="bg-white rounded-lg shadow mb-4">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-3 text-left min-h-[44px]"
      >
        <span className="font-medium text-gray-900">{BASIS_LABEL[basis]}</span>
        <span className="flex items-center gap-3">
          <span className="text-sm text-gray-600 tabular-nums">{count}</span>
          <span className="text-gray-400" aria-hidden="true">{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open && (
        rows.length > 0 ? (
          <>
            <RowTable rows={rows} />
            {pageIncomplete && (
              <p className="px-4 py-2 text-xs text-gray-500">
                Showing {rows.length} of {count} on this page — page through for the rest.
              </p>
            )}
          </>
        ) : (
          <p className="px-4 pb-3 text-sm text-gray-500">
            {count > 0
              ? 'None on this page — page through to reach them.'
              : 'None in this circle.'}
          </p>
        )
      )}
    </section>
  )
}

export default function CoordinationPage() {
  const [place, setPlace] = useState('')
  const [lat, setLat] = useState('')
  const [lon, setLon] = useState('')
  const [radius, setRadius] = useState(String(DEFAULT_RADIUS))
  const [frequency, setFrequency] = useState('')
  const [tolerance, setTolerance] = useState('')
  const [includeBands, setIncludeBands] = useState(true)
  const [activeOnly, setActiveOnly] = useState(true)
  const [page, setPage] = useState(1)

  const [result, setResult] = useState<SitesNearResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    fixed_site: true, mobile_area: false, band_grant: false,
  })

  async function lookupPlace() {
    if (!place.trim()) return
    setError('')
    try {
      const hit = await geocodeQuery(place)
      if (!hit) { setError('No match for that place.'); return }
      setLat(hit.lat)
      setLon(hit.lon)
      setPlace(hit.displayName)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Geocoding failed')
    }
  }

  async function run(toPage = 1) {
    const latNum = parseFloat(lat)
    const lonNum = parseFloat(lon)
    if (Number.isNaN(latNum) || Number.isNaN(lonNum)) {
      setError('Enter a latitude and longitude, or look up a place.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await sitesNear({
        lat: latNum,
        lon: lonNum,
        radius_miles: parseFloat(radius) || DEFAULT_RADIUS,
        frequency_mhz: frequency ? parseFloat(frequency) : undefined,
        tolerance_khz: tolerance ? parseFloat(tolerance) : undefined,
        include_band_authorisations: includeBands,
        active_only: activeOnly,
        page: toPage,
      })
      setResult(data)
      setPage(toPage)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed')
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  const rowsByBasis = (basis: CoordinationBasis) =>
    (result?.rows ?? []).filter((r) => r.basis === basis)

  const totalPages = result ? Math.max(1, Math.ceil(result.total / result.page_size)) : 1

  return (
    <div className="space-y-4">
      {/*
        Persistent and non-dismissible, ABOVE the form rather than below the results.
        A coordination answer that looks authoritative and is not is the worst failure
        this feature has, so the warning cannot be something you scroll past after
        reading the numbers.
      */}
      <div
        role="alert"
        className="rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <strong className="block">In development — not a coordination decision.</strong>
        This search is incomplete. It is a co-channel window over the land mobile records
        we import, with a flat radius and no terrain model. Read the coverage notes below
        before acting on anything here.
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <PageHeading
          title="Frequencies in use near a location" help={COORDINATION_HELP} className="mb-3"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <label htmlFor="coord-place" className="block text-sm font-medium text-gray-700 mb-1">
              Place
            </label>
            <div className="flex gap-2">
              <input
                id="coord-place"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupPlace() } }}
                placeholder="Boulder, CO"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={lookupPlace}
                className="btn btn-secondary whitespace-nowrap"
              >
                Look up
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="coord-lat" className="block text-sm font-medium text-gray-700 mb-1">
              Latitude
            </label>
            <input
              id="coord-lat" value={lat} onChange={(e) => setLat(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>
          <div>
            <label htmlFor="coord-lon" className="block text-sm font-medium text-gray-700 mb-1">
              Longitude
            </label>
            <input
              id="coord-lon" value={lon} onChange={(e) => setLon(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>
          <div>
            <label htmlFor="coord-radius" className="block text-sm font-medium text-gray-700 mb-1">
              Radius (mi)
            </label>
            <input
              id="coord-radius" type="number" min={1} max={MAX_RADIUS} value={radius}
              onChange={(e) => setRadius(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Capped at {MAX_RADIUS} — the §90.621 co-channel separation figure.
            </p>
          </div>
          <div>
            <label htmlFor="coord-freq" className="block text-sm font-medium text-gray-700 mb-1">
              Frequency (MHz)
            </label>
            <input
              id="coord-freq" value={frequency} onChange={(e) => setFrequency(e.target.value)}
              placeholder="all"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>
          <div>
            <label htmlFor="coord-tol" className="block text-sm font-medium text-gray-700 mb-1">
              Tolerance (kHz)
            </label>
            <input
              id="coord-tol" value={tolerance} onChange={(e) => setTolerance(e.target.value)}
              placeholder="1"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-3">
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox" checked={includeBands}
              onChange={(e) => setIncludeBands(e.target.checked)}
              className="rounded border-gray-300"
            />
            Include band authorisations
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox" checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              className="rounded border-gray-300"
            />
            Licenses in force only
          </label>
          <button
            type="button" onClick={() => run(1)} disabled={loading}
            className="btn btn-primary ml-auto"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Band authorisations are ON by default here, unlike the renewal search. The
          question is <em>who may transmit</em>, not <em>who is on this channel</em> — a
          licensee authorised across 150–174 MHz genuinely may be on your candidate
          channel, and leaving them out is the harmful direction.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {result && (
        <>
          <div className="bg-white rounded-lg shadow px-4 py-3 text-sm text-gray-700">
            <strong>{result.total}</strong> assignment{result.total === 1 ? '' : 's'} within{' '}
            {result.radius_miles} miles.
            {result.excluded_inactive > 0 && (
              <span className="block mt-1 text-amber-800">
                {result.excluded_inactive} further assignment
                {result.excluded_inactive === 1 ? '' : 's'} excluded as expired or
                cancelled. An expired license may still have a transmitter radiating —
                untick “Licenses in force only” to see them.
              </span>
            )}
          </div>

          {SECTION_ORDER.map((basis) => (
            <BasisSection
              key={basis}
              basis={basis}
              count={result.counts_by_basis[basis] ?? 0}
              rows={rowsByBasis(basis)}
              open={!!openSections[basis]}
              onToggle={() =>
                setOpenSections((s) => ({ ...s, [basis]: !s[basis] }))
              }
              pageIncomplete={
                rowsByBasis(basis).length < (result.counts_by_basis[basis] ?? 0)
              }
            />
          ))}

          {/* `run(n)` rather than a setState: this page fetches on demand from an
              explicit Search button rather than on every state change, so the page
              number and the request have to move together. */}
          <Pagination page={page} totalPages={totalPages} onPageChange={run} />
        </>
      )}

      {/*
        Always rendered, never collapsible, and shown even before the first search —
        so it cannot be read as a caveat on one particular result.
      */}
      <section className="bg-gray-50 border border-gray-300 rounded-lg p-4">
        <h2 className="font-semibold text-gray-900 mb-2">This search cannot see</h2>
        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
          {(result?.coverage_gaps ?? DEFAULT_GAPS).map((gap) => (
            <li key={gap}>{gap}</li>
          ))}
        </ul>
      </section>
    </div>
  )
}

/**
 * Shown before the first search, so the limits are on screen from the moment the page
 * opens. The server's list is authoritative once a search has run — this is a
 * deliberately shorter stand-in, not a second source of truth.
 */
const DEFAULT_GAPS = [
  'Transmitter records with no coordinates cannot be placed in a circle and do not appear.',
  '800/900 MHz SMR — assignments in 806–901 MHz are recorded almost entirely against area records with no coordinates. This search is close to blind in that band.',
  'Pending applications, and every service outside private/commercial land mobile.',
  'Adjacent-channel and intermodulation conflicts — this is a co-channel window only.',
  'Terrain — distance is a flat radius, not a service contour.',
]
